'use client';

import { useState, useEffect } from 'react';

// Debounce function: Executes the function after a specified time has elapsed
// since the last time it was invoked. This is needed because the Carousel component is invoking this hook 11 times each page load.
function debounce<T extends (...args: any[]) => any>(
  fn: T,
  ms: number
): (...args: Parameters<T>) => void {
  let timer: number | null = null;
  return (...args: Parameters<T>) => {
    if (timer !== null) {
      clearTimeout(timer);
    }
    timer = window.setTimeout(() => {
      fn(...args);
    }, ms);
  };
}

// Refactored useWindowSize.ts
//
// ✅ Uses SSR-safe default values
// ✅ Debounces resize events to avoid rapid state updates
// ✅ Only updates state when size actually changes
export function useWindowSize() {
  const getSize = () => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1920,
    height: typeof window !== 'undefined' ? window.innerHeight : 1080,
  });

  const [windowSize, setWindowSize] = useState(getSize);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = debounce(() => {
      const newSize = getSize();
      setWindowSize((prevSize) => {
        if (
          prevSize.width !== newSize.width ||
          prevSize.height !== newSize.height
        ) {
          return newSize;
        }
        return prevSize;
      });
    }, 150);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}
